import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuIcon, XIcon, GlobeIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
import type { Lang } from '../context/LanguageContext';
const languages: Lang[] = ['UA', 'RU', 'EN'];
const serviceRoutes = ['/services/executive-search', '/services/it-recruitment', '/services/team-building', '/services/career-consulting'];
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const {
    lang,
    setLang
  } = useLanguage();
  const t = useTranslations(lang);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === '/';
  const services = serviceRoutes.map((to, i) => ({
    to,
    label: t.footer.col2_links[i]
  }));
  const hashItems = [{
    label: t.nav.about,
    hash: 'about'
  }, {
    label: t.nav.employers,
    hash: 'process'
  }, {
    label: t.nav.contacts,
    hash: 'contact'
  }];
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToId = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const goToHash = (hash: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMobileOpen(false);
    setServicesOpen(false);
    if (isLanding) {
      scrollToId(hash);
    } else {
      navigate('/');
      window.setTimeout(() => scrollToId(hash), 120);
    }
  };
  const linkCls = isScrolled ? 'text-navy-600 hover:text-primary-600 hover:bg-primary-50' : 'text-navy-700 hover:text-primary-600 hover:bg-white/60';
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-navy-200' : 'bg-transparent'}`} data-id="element-271" data-mp="1ho1l9cj9pq56">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-272" data-mp="18mk6pylidwil">
        <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-273" data-mp="18p697jtjllty">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-id="element-274" data-mp="1cmkjlesylznj">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-sm" data-id="element-275" data-mp="yq7b2v6el8l7">
              <span className="text-white font-extrabold text-xs tracking-wider" data-id="element-276" data-mp="2t2x6xigc0rv8">
                ESCA
              </span>
            </div>
            <div className="flex flex-col" data-id="element-277" data-mp="2y0yrk7teoz8t">
              <span className="font-extrabold text-lg tracking-tight text-navy-900 leading-none" data-id="element-278" data-mp="2xznmm4o85lg4">
                ESCA
              </span>
              <span className="text-[9px] font-medium text-navy-400 tracking-widest uppercase leading-none mt-0.5" data-id="element-279" data-mp="hgcvasbzf12o">
                Executive Search
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation" data-id="element-280" data-mp="rc6jj8z2cj0a">
            <a href="/#about" onClick={goToHash('about')} className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${linkCls}`} data-id="element-281" data-mp="156z5oq9gkxfu">
              {t.nav.about}
            </a>

            {/* Services dropdown */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)} data-id="element-282" data-mp="1a50k2xvxw8kz">
              <button type="button" onClick={() => setServicesOpen(o => !o)} aria-expanded={servicesOpen} aria-haspopup="true" className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${linkCls}`} data-id="element-283" data-mp="3bwx5cz7wswsy">
                {t.nav.services}
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-284" data-mp="2gyyekvqdqm2m" />
              </button>
              <AnimatePresence data-id="element-285" data-mp="1p70phnbkhow1">
                {servicesOpen && <motion.div initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute left-0 top-full pt-2 w-72" data-id="element-286" data-mp="1p566iejuw6ul">
                    <div className="bg-white rounded-xl shadow-xl shadow-navy-900/10 border border-navy-200 overflow-hidden py-1.5" data-id="element-287" data-mp="15ihugp3vrh9f">
                      {services.map(s => <Link key={s.to} to={s.to} onClick={() => setServicesOpen(false)} className={`flex items-center justify-between gap-2 px-4 py-2.5 text-sm transition-colors ${location.pathname === s.to ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-navy-700 hover:bg-navy-50'}`} data-id="element-288" data-mp="t0xdyswgrm5u">
                          {s.label}
                          <ChevronRightIcon className="w-3.5 h-3.5 text-navy-300 flex-shrink-0" data-id="element-289" data-mp="58qj5y6dzn8j" />
                        </Link>)}
                      <div className="border-t border-navy-100 mt-1.5 pt-1.5" data-id="element-290" data-mp="3sehj6fnpd08g">
                        <a href="/#services" onClick={goToHash('services')} className="block px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors" data-id="element-291" data-mp="3ngg0hvu6v2xi">
                          {t.nav.services} →
                        </a>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <a href="/#process" onClick={goToHash('process')} className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${linkCls}`} data-id="element-292" data-mp="dxn7mwzpsdr">
              {t.nav.employers}
            </a>
            <a href="/#faq" onClick={goToHash('faq')} className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${linkCls}`} data-id="element-293" data-mp="3mrpfr0i66o75">
              {t.faq.title}
            </a>
            <a href="/#contact" onClick={goToHash('contact')} className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${linkCls}`} data-id="element-294" data-mp="o4bjjpmlyde5">
              {t.nav.contacts}
            </a>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3" data-id="element-295" data-mp="2iki8dui9u66k">
            {/* Language Switcher */}
            <div className="relative hidden sm:block" data-id="element-296" data-mp="2dl5nhfayn96m">
              <button onClick={() => setLangOpen(!langOpen)} className={`flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${isScrolled ? 'text-navy-500 hover:text-navy-700 hover:bg-navy-100' : 'text-navy-600 hover:text-navy-800 hover:bg-white/60'}`} aria-label="Select language" data-id="element-297" data-mp="2dlp66y8fq1lp">
                <GlobeIcon className="w-4 h-4" data-id="element-298" data-mp="1utmlnbsoa166" />
                {lang}
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} data-id="element-299" data-mp="3aamzec0jdrgr" />
              </button>
              <AnimatePresence data-id="element-300" data-mp="36bxe7x99b5mk">
                {langOpen && <motion.div initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-navy-200 overflow-hidden min-w-[80px]" data-id="element-301" data-mp="13ogzpf0wruri">
                    {languages.map(l => <button key={l} onClick={() => {
                  setLang(l);
                  setLangOpen(false);
                }} className={`block w-full text-left px-4 py-2 text-sm transition-colors ${lang === l ? 'bg-primary-50 text-primary-700 font-medium' : 'text-navy-600 hover:bg-navy-50'}`} data-id="element-302" data-mp="1mfrqzxajq9jx">
                        {l}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* CTA Button */}
            <a href="/#contact" onClick={goToHash('contact')} className="hidden sm:inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm shadow-primary-600/20" data-id="element-303" data-mp="2ffy3rzh3pwur">
              {t.header.cta}
            </a>

            {/* Mobile toggle */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 rounded-lg text-navy-600 hover:bg-navy-100 transition-colors" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} data-id="element-304" data-mp="2z3ns1wptxez8">
              {mobileOpen ? <XIcon className="w-5 h-5" data-id="element-305" data-mp="1ipnw0wu3u9h9" /> : <MenuIcon className="w-5 h-5" data-id="element-306" data-mp="1inujj0wt8v1c" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-307" data-mp="1d24i541sav5d">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="lg:hidden bg-white border-t border-navy-200 overflow-hidden max-h-[calc(100svh-4rem)] overflow-y-auto" data-id="element-308" data-mp="1d0tfx8nzlvgd">
            <nav className="px-4 py-4 space-y-1" aria-label="Mobile navigation" data-id="element-309" data-mp="1vrubrntt9avp">
              <a href="/#about" onClick={goToHash('about')} className="block px-4 py-3 text-sm font-medium text-navy-700 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors" data-id="element-310" data-mp="1qtapfmbb1x2t">
                {t.nav.about}
              </a>

              <div className="pt-2" data-id="element-311" data-mp="bed8boz3my5x">
                <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-navy-400" data-id="element-312" data-mp="bfet4l5x2z0u">
                  {t.nav.services}
                </p>
                {services.map(s => <Link key={s.to} to={s.to} onClick={() => setMobileOpen(false)} className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${location.pathname === s.to ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-navy-700 hover:bg-navy-50'}`} data-id="element-313" data-mp="2i46u501ueezv">
                    {s.label}
                  </Link>)}
              </div>

              {hashItems.slice(1).map(item => <a key={item.hash} href={`/#${item.hash}`} onClick={goToHash(item.hash)} className="block px-4 py-3 text-sm font-medium text-navy-700 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors" data-id="element-314" data-mp="1uc8de0jmjrfc">
                  {item.label}
                </a>)}
              <a href="/#faq" onClick={goToHash('faq')} className="block px-4 py-3 text-sm font-medium text-navy-700 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors" data-id="element-315" data-mp="3n70x1tkjz8eb">
                {t.faq.title}
              </a>

              <div className="pt-3 border-t border-navy-100 mt-3 flex items-center gap-2" data-id="element-316" data-mp="38dxt4ozvwt62">
                {languages.map(l => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${lang === l ? 'bg-primary-600 text-white font-medium' : 'text-navy-500 hover:bg-navy-100'}`} data-id="element-317" data-mp="4ukxmvhghfxv">
                    {l}
                  </button>)}
              </div>
              <a href="/#contact" onClick={goToHash('contact')} className="block mt-3 text-center px-5 py-3 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors" data-id="element-318" data-mp="ntywnru1lgv8">
                {t.header.cta}
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}